<template>
  <div
    class="sky__product-card relative"
    @click="$emit('click')"
  >
    <nuxt-link
      :style="{
        paddingTop: cover ? '' : '100%',
        backgroundSize: 'cover',
        backgroundImage: background ? `url(${background}?resize=true&width=300&height=300&format=webp)` : undefined
      }"
      class="bg-sky_grey_darker mb-3 relative cursor-pointer block"
      :to="$helpers.makeUri(path)"
      :title="label"
    >
      <img
        v-if="cover"
        class="sky__product-card-cover"
        :class="['sky__product-card-cover-animation_' + effect, animated ? 'animated' : '']"
        :src="$helpers.makeCdnUrl(cover, slug, 'c', 'pc')"
        :alt="label"
        loading="lazy"
        width="300"
        height="300"
        @mouseenter="startAnimation()"
      >
      <div v-if="discountPercentage" class="absolute font-funny bg-sky_orange text-white text-xl rounded-full px-2 py-2" style="bottom: 10px; right: 10px;">
        -{{ discountPercentage }}%
      </div>
    </nuxt-link>
    <div class="px-2">
      <div class="sky__product-name">
        <nuxt-link :to="$helpers.makeUri(path)">
          {{ label }}
        </nuxt-link>
      </div>
      <div
        class="flex mb-3 items-center justify-between"
        style="height: 16px;"
      >
        <div
          v-if="reviewsAverage && totalReviews"
          class="flex items-center"
          :style="{
            opacity: totalReviews === 0 ? 0 : 1
          }"
        >
          <notation
            :notation="reviewsAverage"
            :max="5"
          />
          <span class="ml-1 text-xxs">
            ({{ totalReviews }})
          </span>
        </div>
      </div>
      <div v-if="price" class="flex justify-between items-center">
        <div class="flex items-center">
          <div
            class="text-lg font-bold"
            :class="{
              'text-sky_secondary': price !== crossedPrice
            }"
          >
            {{ $helpers.formatCurrency(price) }}
          </div>
          <div v-if="price !== crossedPrice" class="ml-2 text-xs hover:no-underline line-through text-gray-400">
            {{ $helpers.formatCurrency(price) }}
          </div>
        </div>
        <div
          v-if="stockStatus === 'available'"
          class="text-success text-xs flex items-center"
          style="height: 38px;"
        >
          <nuxt-link :to="$helpers.makeUri(path)">
            En stock
          </nuxt-link>
        </div>
        <div v-else-if="stockStatus === 'available_later'" class="text-sky_orange flex items-center text-xs" style="height: 38px;">
          <nuxt-link :to="$helpers.makeUri(path)">
            Commander
          </nuxt-link>
        </div>
        <div v-else-if="stockStatus === 'out_of_stock'" class="text-red-500 flex items-center text-xs" style="height: 38px;">
          Rupture
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import PaperPlane from './icons/PaperPlane'
import Notation from './Notation'

export default {
  components: {
    PulseLoader,
    PaperPlane,
    Notation
  },
  props: {
    background: {
      required: false,
      type: String,
      default: null
    },
    cover: {
      required: false,
      type: String,
      default: null
    },
    label: {
      required: true,
      type: String
    },
    totalReviews: {
      required: false,
      type: Number,
      default: null
    },
    reviewsAverage: {
      required: false,
      type: Number,
      default: null
    },
    price: {
      required: true,
      type: Number
    },
    crossedPrice: {
      required: false,
      type: Number,
      default: null
    },
    stockStatus: {
      required: false,
      type: String,
      default: 'out_of_stock'
    },
    path: {
      required: true,
      type: Array
    },
    effect: {
      required: false,
      type: String,
      default: null
    },
    slug: {
      required: false,
      type: String,
      default: 'slug'
    }
  },
  data () {
    return {
      animated: false
    }
  },
  computed: {
    headband () {
      return this.product.properties.headband
    },
    discountPercentage () {
      if (!this.price) {
        return null
      }
      const percentage = (this.crossedPrice - this.price) / this.crossedPrice * 100
      return Math.ceil(percentage) || null
    }
  },
  methods: {
    startAnimation () {
      if (this.animated) {
        return false
      }
      this.animated = true

      setTimeout(() => {
        this.animated = false
      }, 5000)
    }
  }
}
</script>

<style lang="scss" scoped>
.sky__product-card {
  @apply pb-3 shadow-none;
  transition-duration: 0.2s;
  min-width: 155px;
  &.is-hovered {
    @apply shadow-md;
  }
  &:hover {
    @apply shadow-md;
  }
  .sky__product-card-cover {
    @apply block w-full;
    &.sky__product-card-cover-animation_vertical_illumination {
      transform: translateY(8px);
      &.animated {
        animation: wobble-vertical 5s infinite;
        animation-timing-function: ease-in-out;
        filter: drop-shadow(0px 0px 8px lightyellow);
      }
    }
    &.sky__product-card-cover-animation_illumination,&.sky__product-card-cover-animation_pendule_illumination {
      &.animated {
        filter: drop-shadow(0px 0px 8px lightyellow);
      }
    }
    &.sky__product-card-cover-animation_vertical {
      transform: translateY(8px);
      &.animated {
        animation: wobble-vertical 5s infinite;
        animation-timing-function: ease-in-out;
      }
    }
    &.sky__product-card-cover-animation_pendule,&.sky__product-card-cover-animation_pendule_illumination{
      &.animated {
        margin:0 auto;
        display:block;
        animation: pendulum;
        animation-iteration-count: 1;
        animation-duration: 5s;
        transform-origin: top center;
      }
    }
    &.sky__product-card-cover-animation_maracasse {
      &.animated {
        margin:0 auto;
        display:block;
        animation: chikachika;
        animation-iteration-count: 1;
        animation-duration: 1.2s;
        transform-origin: center center;
      }
    }
    &.sky__product-card-cover-animation_horizontal {
      transform: translateZ(0);
      box-shadow: 0 0 1px rgba(0, 0, 0, 0);
      &.animated {
        animation-name: wobble-horizontal;
        animation-duration: 5s;
        animation-timing-function: ease-in-out;
        animation-iteration-count: 1;
      }
    }
  }

  .sky__product-card-add-product-btn {
    @apply cursor-pointer;
    width: 38px;
    height: 38px;
    .paper-plane {
      @apply hidden;
    }
    &:hover {
      .cart-icon {
        @apply hidden;
      }
      .paper-plane-icon {
        @apply block;
      }
    }
  }
}
.sky__product-name {
  @apply font-medium leading-snug mb-2 text-xs overflow-hidden;
  height: 45px;
  @screen md {
    @apply text-sm;
  }
  @screen lg {
    height: 40px;
    font-size:14px;
  }
}
.text-special-lg {
  @screen lg {
    font-size:14px;
  }
}
.sky__product-card-decreasing-prices {
  @apply hidden;
  @screen lg {
    @apply absolute block left-0 right-0 bg-white z-10 shadow-lg;
    top: 100%;
  }
  .sky__decreasing-price {
    @apply text-sm py-1 px-2 flex justify-between items-center;
    &:nth-child(odd) {
      @apply bg-sky_grey_darker;
    }
    &:nth-child(odd) {
      @apply bg-sky_grey_darker;
    }
    &:hover {
      @apply bg-sky_blue text-white;
      path {
        stroke: white;
        fill: white;
      }
    }
  }
}

.paper-plane-icon-animated {
  @apply relative z-50;
  transform: translate3D(800px, -800px, 0);
}
.cart-icon-transition-enter-active {
  animation: 1s cart-icon-animation;
  // animation-timing-function: ease-out;
}

.cart-icon-transition-enter {
  // transform: translateX(0);
}

@keyframes chikachika {
  from { transform: rotate(0deg); }
  1%  { transform: rotate(-8deg); }
  5%  { transform: rotate(8deg); }
  10%  { transform: rotate(-6deg); }
  15%  { transform: rotate(5deg); }
  20%  { transform: rotate(-4deg); }
  25%  { transform: rotate(3deg); }
  30%  { transform: rotate(0deg); }
  to   { transform: rotate(0deg); }
}

@keyframes cart-icon-animation {
  0% {
    transform: translate3D(0, 0, 0);
  }
  20% {
    transform: translate3D(-20px, 20px, 0);
    opacity: 1;
  }
  100% {
    transform: translate3D(800px, -800px, 0);
    opacity: 0.2;
  }
}

@keyframes wobble-vertical {
  16.65% {
    transform: translateY(16px);
  }

  33.3% {
    transform: translateY(2px);
  }

  49.95% {
    transform: translateY(12px);
  }

  66.6% {
    transform: translateY(6px);
  }

  83.25% {
    transform: translateY(9px);
  }

  100% {
    transform: translateY(8px);
  }
}

@keyframes wobble-horizontal {
  16.65% {
    -webkit-transform: translateX(8px);
    transform: translateX(8px);
  }

  33.3% {
    -webkit-transform: translateX(-6px);
    transform: translateX(-6px);
  }

  49.95% {
    -webkit-transform: translateX(4px);
    transform: translateX(4px);
  }

  66.6% {
    -webkit-transform: translateX(-2px);
    transform: translateX(-2px);
  }

  83.25% {
    -webkit-transform: translateX(1px);
    transform: translateX(1px);
  }

  100% {
    -webkit-transform: translateX(0);
    transform: translateX(0);
  }
}

@keyframes pendulum {
  from { transform: rotate(0deg); }
  12%  { transform: rotate(-9deg); }
  27%  { transform: rotate(8deg); }
  39%  { transform: rotate(-6deg); }
  52%  { transform: rotate(4deg); }
  62%  { transform: rotate(-3deg); }
  73%  { transform: rotate(2deg); }
  80%  { transform: rotate(-1deg); }
  88%  { transform: rotate(1deg); }
  94%  { transform: rotate(-0.5deg); }
  98%  { transform: rotate(0.5deg); }
  to   { transform: rotate(0deg); }
}
</style>
